
import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Socket } from 'socket.io-client';
import { GameState } from '../../types/game.types';
import { V3GameState, V3Player } from '../../types/V3_Core';
import { sessionManager } from '../../utils/sessionManager';
import VerdictModal from './VerdictModal';
import HelpModal from '../ui/HelpModal';
import DecorLayer from '../effects/DecorLayer';
import { useTheme } from '../../context/ThemeContext';
import { audioManager } from '../../utils/AudioManager';
import GameArenaUI from './GameArenaUI';

interface GameArenaProps {
    gameState: GameState | null;
    socket: Socket | null;
}

const handleProbe = (socket: Socket | null, roomId: string, targetId: string, cellIndex: number) => {
    if (!socket) return;
    // Play click SFX
    audioManager.playSFX('click');
    socket.emit('V3_ACTION_PROBE', {
        roomId,
        targetId,
        cellId: cellIndex
    });
};

const handleVerdict = (socket: Socket | null, roomId: string, isCorrect: boolean) => {
    if (!socket) return;
    // Play verdict SFX
    audioManager.playSFX(isCorrect ? 'success' : 'error');
    socket.emit('V3_ACTION_VERDICT', {
        roomId,
        isCorrect
    });
};

const AVATAR_MAP: Record<string, string> = {
    'esteban': 'Stratège',
    'hortense': 'Analyste',
    'tao': 'Optimisateur',
    'constance': 'Gardienne',
    'greg': 'Créatif',
    'sofia': 'Exploratrice',
    'leo': 'Joueur',
    'zia': 'Intuitive',
    'default': 'Enquêteur'
};

const GameArena: React.FC<GameArenaProps> = ({ gameState, socket }) => {
    const v3State = (gameState as any)?.v3State as V3GameState | undefined;
    const localPlayerId = sessionManager.getPlayerId();
    const [showHelp, setShowHelp] = useState(false);

    const { opponents, localPlayer, activePlayerId } = useMemo(() => {
        if (!v3State || !v3State.players) return { opponents: [], localPlayer: null, activePlayerId: null };
        const players = v3State.players;
        const local = players.find(p => p.id === localPlayerId) || null;
        const others = players.filter(p => p.id !== localPlayerId);
        return { opponents: others, localPlayer: local, activePlayerId: v3State.activePlayerId };
    }, [v3State, localPlayerId]);



    // Listen for probe results to play appropriate SFX
    useEffect(() => {
        if (!socket) return;

        const handleProbeResult = (data: any) => {
            if (data.status === 'REVEALED_HIT') {
                audioManager.playSFX('fragment');
            } else if (data.status === 'SHADOW_HIT') {
                audioManager.playSFX('shadow');
            }
            // REVEALED_EMPTY plays no sound (just click)
        };

        socket.on('V3_PROBE_RESULT', handleProbeResult);

        return () => {
            socket.off('V3_PROBE_RESULT', handleProbeResult);
        };
    }, [socket]);

    if (!v3State) return (
        <div className="flex items-center justify-center h-screen bg-[#020617] text-cyan-500 font-mono animate-pulse">
            INITIALISATION DU PLATEAU...
        </div>
    );

    const isMyTurn = activePlayerId === localPlayerId;

    const { currentTheme, setTheme, availableThemes } = useTheme();

    // ADMIN THEME SWITCHER (DevTools)
    // Toggle next theme
    const toggleTheme = () => {
        const currentIndex = availableThemes.findIndex(t => t.id === currentTheme.id);
        const nextIndex = (currentIndex + 1) % availableThemes.length;
        setTheme(availableThemes[nextIndex].id || 'default');
    };

    return (
        <GameArenaUI
            v3State={v3State}
            localPlayerId={localPlayerId || ''}
            onProbe={(targetId: string, cellIndex: number) => handleProbe(socket, v3State.roomId, targetId, cellIndex)}
            onVerdict={(isCorrect: boolean) => handleVerdict(socket, v3State.roomId, isCorrect)}
        />
    );
};

export default GameArena;
